// Studio helpers for the TFI #3340 buckets on top of the shared host map in
// ./media-hosts (vendored byte-for-byte from iv-soa docs/gen2-architecture/media-hosts).
// Every helper only swaps the host: the S3 object key is never changed.
import {
  bucketAndKey,
  canonicalUrl,
  CDN_HOST_TO_BUCKET,
  MEDIA_HOSTS_BUCKETS,
  MEDIA_HOSTS_EMIT_INVIDEO_IO,
  unresizedUrl,
} from './media-hosts/mediaHosts';
import { toLockedBucketCdnUrl, toOtherLockedBucketCdnUrl } from './lockedBucketsCdn';
import { isResizedUploadsCdnUrl, toUploadsCdnUrl } from './uploadsCdn';

export { bucketAndKey, canonicalUrl, MEDIA_HOSTS_EMIT_INVIDEO_IO, unresizedUrl };

// Rollout switches, one per part so each can be reverted alone with a one-line change.
// W-FE-1: a media URL that fails to load is retried once: a resized (`<N>x<N>/`) copy as
// its original, a raw S3 URL as its CDN spelling.
export const RESIZED_IMAGE_FALLBACK = true;
// W-FE-2: getCloudfrontUrl maps every spelling (special-character keys included) to the CDN.
export const CANONICAL_CLOUDFRONT_URL = true;
// W-FE-3: media-processing URLs (invideo-images frames) go out as CDN URLs, and the
// optimisers send vhost and website spellings to the CDN (same key, no resize).
export const MEDIA_PROCESSING_CDN_URLS = true;
export const VHOST_AWARE_OPTIMISERS = true;
// W-FE-4: raw S3 display defaults are used in their CDN form; placeholder lists also
// match the CDN spellings.
export const CDN_DEFAULT_MEDIA_URLS = true;
// W-FE-5: a resized uploads copy that fails to load is retried once as its full-size
// original (resized copies are not generated on demand, and the original's folder is not
// in the resized URL).
export const UPLOADS_THUMBNAIL_ORIGINAL_FALLBACK = true;
// W-FE-6: a preview copy (`<N>x<N>/`, `previews-480/`, `compressed-audio/`) that fails to
// load is retried once as the original it was built from, given by the caller. Missing
// copies are not generated (OPS-061). Off: the editor slate carries no originals and the
// image, video and audio handlers below retry nothing new.
export const PREVIEW_COPY_ORIGINAL_FALLBACK = true;

// every CDN host (CloudFront ids and invideo.io aliases) of the five buckets
export const MEDIA_CDN_HOSTS: string[] = Object.keys(CDN_HOST_TO_BUCKET);

const PATH_STYLE_HOST = /^s3[.-]/;

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/\.+$/, '');
  } catch (e) {
    return null;
  }
}

/** True for a URL on any CDN host (CloudFront id or invideo.io alias) of the five buckets. */
export function isMediaCdnUrl(url: unknown): boolean {
  if (typeof url !== 'string') {
    return false;
  }
  const host = hostOf(url);
  return !!host && host in CDN_HOST_TO_BUCKET && canonicalUrl(url) !== null;
}

/**
 * Optimiser input: a raw vhost or website spelling of the five buckets, which the
 * optimisers cannot split (they read the bucket from path segment 1), mapped to the
 * CDN with the same key and no resize. Null for anything else, so the optimisers
 * handle path-style, CDN and other URLs exactly as before.
 */
export function vhostMediaCdnUrl(url: string): string | null {
  if (!VHOST_AWARE_OPTIMISERS || typeof url !== 'string' || !url.includes('amazonaws.com')) {
    return null;
  }
  const host = hostOf(url);
  // raw S3 hosts only: CDN URLs (even with amazonaws.com in the path) keep today's handling
  if (!host || !host.endsWith('.amazonaws.com') || PATH_STYLE_HOST.test(host)) {
    return null;
  }
  return canonicalUrl(url);
}

/** A raw S3 default or placeholder URL in its CDN form (unchanged while CDN_DEFAULT_MEDIA_URLS is off). */
export function cdnDefaultMediaUrl(url: string): string {
  return (CDN_DEFAULT_MEDIA_URLS && canonicalUrl(url)) || url;
}

/**
 * A placeholder URL list plus the CDN spellings (every CDN host, same key) of its
 * entries in the five buckets, so stored CDN URLs match too. The raw entries keep
 * their order (index 0 stays the default). Unchanged while CDN_DEFAULT_MEDIA_URLS is off.
 */
export function withMediaCdnSpellings(urls: string[]): string[] {
  if (!CDN_DEFAULT_MEDIA_URLS) {
    return urls;
  }
  const spellings = new Set(urls);
  urls.forEach((url) => {
    const canonical = canonicalUrl(url);
    const media = bucketAndKey(url);
    if (!canonical || !media) {
      return;
    }
    const path = canonical.replace(/^https:\/\/[^/]+\//, '').replace(/[?#].*$/, '');
    MEDIA_HOSTS_BUCKETS[media[0]].cdn_hosts.forEach((host) =>
      spellings.add(`https://${host}/${path}`),
    );
  });
  return Array.from(spellings);
}

/**
 * A raw S3 URL of any locked bucket (see toLockedBucketCdnUrl) on its CDN host (same
 * key), and a CDN URL of the five buckets on their emitted host; anything else, CDN
 * URLs of the other buckets included, unchanged.
 */
export function toMediaCdnUrl(url: string): string {
  const lockedCdnUrl = toLockedBucketCdnUrl(url);
  if (lockedCdnUrl !== url) {
    return lockedCdnUrl;
  }
  return canonicalUrl(url) || url;
}

const RETRIED_ATTRIBUTE = 'data-iv-media-retry';
const RETRIED_FROM_ATTRIBUTE = 'data-iv-media-retry-from';
const ORIGINAL_RETRIED_ATTRIBUTE = 'data-iv-media-original-retry';

// CDN of the previews-480 bucket (keys `<bucket>/<key>`); every path is a copy
const PREVIEWS_480_CDN_HOST = 'dulpz3imcxnof.cloudfront.net';
// copies in the source bucket: `<N>x<N>/<name>`, `previews-480/<name>`, `compressed-audio/<name>`
const PREVIEW_COPY_PATH = /^\/(\d+x\d+|previews-480|compressed-audio)\//;

/** True for a resized, preview or compressed copy on a studio media CDN host. */
export function isPreviewCopyUrl(url: unknown): boolean {
  if (typeof url !== 'string') {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return false;
  }
  if (parsed.protocol !== 'https:') {
    return false;
  }
  const host = parsed.hostname.toLowerCase();
  if (host === PREVIEWS_480_CDN_HOST) {
    return true;
  }
  return (
    (host.endsWith('.cloudfront.net') || host.endsWith('.invideo.io')) &&
    PREVIEW_COPY_PATH.test(parsed.pathname)
  );
}

/**
 * The original (on the CDN) to try once when the preview copy `url` fails to load.
 * `originalUrl` is the URL the copy was built from. Null when the switch is off, `url`
 * is not a copy, or the original is itself a copy (nothing better to try).
 */
export function previewCopyOriginalUrl(url: string, originalUrl?: string | null): string | null {
  if (!PREVIEW_COPY_ORIGINAL_FALLBACK || !originalUrl || !isPreviewCopyUrl(url)) {
    return null;
  }
  const original = toMediaCdnUrl(originalUrl);
  return original !== url && !isPreviewCopyUrl(original) ? original : null;
}

/**
 * The full-size original (on the CDN) to try when a resized uploads copy fails to
 * load. `originalUrl` must be an image: callers never pass a video. Null otherwise.
 */
export function uploadsThumbnailOriginalUrl(
  url: string,
  originalUrl?: string | null,
): string | null {
  if (!UPLOADS_THUMBNAIL_ORIGINAL_FALLBACK || !originalUrl || !isResizedUploadsCdnUrl(url)) {
    return null;
  }
  const original = toMediaCdnUrl(originalUrl);
  return original !== url ? original : null;
}

/**
 * The URL to try once when a media URL of the five buckets fails to load: the
 * original of a resized copy, else the CDN spelling of a raw S3 URL. A raw S3 URL
 * of the other locked buckets (upload buckets, legacy uploads, thumbnails, static
 * assets) is tried on its CDN host. Null when there is nothing else to try (other
 * buckets, CDN originals).
 */
export function mediaFallbackUrl(url: string): string | null {
  const uploadsCdnUrl = toUploadsCdnUrl(url);
  if (uploadsCdnUrl !== url) {
    return uploadsCdnUrl;
  }
  const lockedCdnUrl = toOtherLockedBucketCdnUrl(url);
  if (lockedCdnUrl !== url) {
    return lockedCdnUrl;
  }
  if (!RESIZED_IMAGE_FALLBACK) {
    return null;
  }
  const fallback = unresizedUrl(url) ?? canonicalUrl(url);
  return fallback && fallback !== url ? fallback : null;
}

/**
 * <img> error handler: retries a failed media URL once (see mediaFallbackUrl). With
 * `originalUrl` (an image), a resized uploads copy that fails, or whose retry fails,
 * is tried once more as that original (see uploadsThumbnailOriginalUrl), and so is any
 * other preview copy (see previewCopyOriginalUrl). At most two retries per source.
 * Returns true when a retry was started, so the caller's own error handling waits
 * for it. Missing resized copies and raw S3 reads are a 403 once the buckets are private
 * (the upload buckets already are).
 * The retry is tracked per source, so a reused element can retry its next source too.
 */
export function retryMediaImage(target: EventTarget | null, originalUrl?: string | null): boolean {
  if (typeof HTMLImageElement === 'undefined' || !(target instanceof HTMLImageElement)) {
    return false;
  }
  const src = target.currentSrc || target.src;
  const retried = target.getAttribute(RETRIED_ATTRIBUTE);
  const isRetry = retried === src;
  // the copy the element was given: a failed retry of it (e.g. its unresized key) still has the original left
  const source = (isRetry && target.getAttribute(RETRIED_FROM_ATTRIBUTE)) || src;
  // once the retried URL itself failed, only the original is left to try
  const fallback =
    (!isRetry ? mediaFallbackUrl(src) : null) ??
    uploadsThumbnailOriginalUrl(src, originalUrl) ??
    previewCopyOriginalUrl(source, originalUrl);
  if (!fallback || fallback === retried || fallback === src) {
    return false;
  }
  target.setAttribute(RETRIED_ATTRIBUTE, fallback);
  if (!isRetry) {
    target.setAttribute(RETRIED_FROM_ATTRIBUTE, src);
  }
  target.src = fallback;
  return true;
}

/**
 * <video> / <audio> / <source> error handler: a preview copy (see isPreviewCopyUrl) that
 * fails to load is swapped once for its original (`originalUrl`, mapped to the CDN). A
 * <source> reloads its media element. Returns true when a retry was started, so the
 * caller's own error handling waits for it. Never retries the original itself.
 */
export function retryMediaPreview(
  target: EventTarget | null,
  originalUrl?: string | null,
): boolean {
  const isMedia = typeof HTMLMediaElement !== 'undefined' && target instanceof HTMLMediaElement;
  const isSource = typeof HTMLSourceElement !== 'undefined' && target instanceof HTMLSourceElement;
  if (!isMedia && !isSource) {
    return false;
  }
  const element = target as HTMLMediaElement | HTMLSourceElement;
  const src = (isMedia && (element as HTMLMediaElement).currentSrc) || element.src;
  const fallback = previewCopyOriginalUrl(src, originalUrl);
  // the original we swapped to failed too (a copy whose original is a copy returns null above)
  if (!fallback || element.getAttribute(ORIGINAL_RETRIED_ATTRIBUTE) === src) {
    return false;
  }
  element.setAttribute(ORIGINAL_RETRIED_ATTRIBUTE, fallback);
  element.src = fallback;
  if (isSource) {
    const media = element.parentElement;
    if (typeof HTMLMediaElement !== 'undefined' && media instanceof HTMLMediaElement) {
      media.load();
    }
  }
  return true;
}

/**
 * Preloads a media URL used as a CSS background and resolves to the URL that
 * loads (mediaFallbackUrl when the URL itself fails).
 */
export function loadableMediaImageUrl(url: string): Promise<string> {
  const fallback = mediaFallbackUrl(url);
  if (!fallback || typeof Image === 'undefined') {
    return Promise.resolve(url);
  }
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => resolve(fallback);
    image.src = url;
  });
}
